import { newRequest } from "./complexRequest"

// 大众审核（后端 hiwaifu_php `/client/review/*`，登录态沿用 App token）

export type ReviewStage = 0 | 1 | 2 // 0 申请中 1 见习 2 正式

export interface ReviewerInfo {
  stage: ReviewStage
  level: number
  disabled: boolean
  integral: number
  /** expires_at：见习截止时间（秒），满了仍未转正即取消资格；非见习为 0 */
  probation: { done: number; need: number; gold: number; gold_hit: number; pass_rate: number; expires_at: number }
}

/** 邀请门槛：nsfw 未确认成年 / 未开 NSFW，new_account 注册不满 7 天，violation 有违规记录或低信用 */
export type InviteReason = 'nsfw' | 'new_account' | 'violation'

export interface ExamState {
  question_count: number
  pass_rate: number
  daily_attempts: number
  used_24h: number
  next_available_at: number
  last: { correct: number; total: number; passed: boolean; at: number } | null
}

export interface ReviewMe {
  user: { uid: number; name: string; avatar: string; friend_code: string }
  reviewer: ReviewerInfo | null
  /** 还不是审核员时才有：能否看到邀请入口 / 申请 */
  invite: { eligible: boolean; reasons: InviteReason[] } | null
  /** 是否在规则页确认过已读（首次考试前必须） */
  rules_read: boolean
  exam: ExamState
  unread_notices: number
}

export interface ReviewRobot {
  name: string
  avatar: string
  description: string
  greeting: string
  scene: string
  guide: string
  attributes: { k: string; v: string }[]
  conversations: string
  /** 聊天背景图（main.robot_config）；情景图片任务与旧快照没有 */
  background?: string
  lorebooks?: { id: number; title: string; cover: string; description: string }[]
}

export interface ReviewReport {
  reason: string
  detail: string
  count?: number
  /** 举报原因串解析出的多语言码；unknown 为认不出的原文片段 */
  tags?: { reason: string | null; parts: string[]; unknown: string[] }
}

export interface TranslatedContent {
  lang: string
  robot?: Partial<Omit<ReviewRobot, 'avatar'>>
  report?: Partial<ReviewReport> | null
  lorebook?: { title: string; description: string; entries: { title: string; content: string }[] }
  image?: { trigger: string }
}

/** audit / report 为机器人；其余为 Lorebook 公开版本、Lorebook 举报、情景图片 */
export type TaskKind = 'audit' | 'report' | 'lorebook' | 'lorebook_report' | 'scene_image'

export interface LorebookView {
  title: string
  description: string
  cover: string
  entry_count: number
  entries: { title: string; keywords: string[]; content: string }[]
}

/** 角色卡上绑定的 Lorebook 展开（只读）；translated 为请求了 lang 时的译文 */
export interface AttachedLorebook {
  lorebook: LorebookView
  translated: NonNullable<TranslatedContent['lorebook']> | null
}

export interface SceneImageView {
  url: string
  full_url: string
  trigger: string
  emotion: string
  source: 'ai' | 'upload'
}

export interface ReviewTask {
  token: string
  kind: TaskKind
  locked_until: number
  submitted_at: number
  /** 机器人任务是完整角色；情景图片任务只有 name / avatar / scene 作上下文 */
  robot: ReviewRobot
  report: ReviewReport | null
  lorebook?: LorebookView
  image?: SceneImageView
  /** 非机器人任务的可选违规位置 */
  positions?: string[]
  /** 仅高级审核员的机器人真实单：创作者身份，方便查可疑用户 */
  creator?: TaskCreator
}

export interface TaskCreator {
  uid: number
  name: string
  avatar: string
  friend_code: string
  attention_high: boolean
}

export interface TaskProgress {
  today: number
  integral?: number
  stage?: ReviewStage
  probation?: ReviewerInfo['probation']
}

export interface ReviewHints {
  text_nsfw: number
  avatar_nsfw: number
  /** 背景图机审；null = 没有背景图 */
  background_nsfw?: number | null
  home: boolean
}

export type Outcome = 'pass' | 'reject' | 'upheld' | 'dismissed' | 'secondary' | null

export interface SubmitResult {
  outcome: Outcome
  hints: ReviewHints
  probation?: { promoted: boolean; round_reset: boolean }
  progress: TaskProgress
}

export interface ReviewRules {
  reasons: string[]
  positions: string[]
  exam: { question_count: number; pass_rate: number; daily_attempts: number }
  probation: { tasks: number; pass_rate: number }
  task_lock_minutes: number
  /** 规则页「平台怎么运作」用的数字 */
  platform: { voters: number; reward: number; deduct: number; report_deposit: number; report_back: number; gold_ratio: number; probation_days: number }
}

export interface ExamQuestion {
  qid: number
  kind: 'audit' | 'report'
  robot: ReviewRobot
  report: ReviewReport | null
}

export interface ExamPaper {
  attempt_id: number
  started_at: number
  questions: ExamQuestion[]
}

export interface ExamAnswer { violation: boolean; reason?: string }

export interface ExamResult {
  correct: number
  total: number
  passed: boolean
  pass_rate: number
  review: {
    qid: number
    correct: boolean
    your: { violation: boolean | null; reason: string }
    /** 答错时该重读的规则：违规原因码 / general / allowed（NSFW 角色允许的内容）；不下发标准答案 */
    rule: string | null
  }[]
  exam: ExamState
  /** 考试记录回看时才有 */
  submitted_at?: number
}

export interface ExamHistoryItem {
  attempt_id: number
  started_at: number
  submitted_at: number
  correct: number
  total: number
  passed: boolean
}

/** hidden = 举报后运营限流：角色在线但不进搜索推荐，按违规计 */
export type FinalView = 'violation' | 'hidden' | 'clean' | 'secondary' | 'pending'

export interface ReviewRecord {
  id: number
  /** 机器人审核 / 举报记录对应的审核单 id（点开看审核单详情）；Lorebook / 情景图片记录没有，按 id 看 */
  case_id?: number
  kind: TaskKind
  robot_id: number
  robot_name: string
  robot_avatar: string
  my_vote: 'violation' | 'clean' | 'timeout' | 'none'
  my_reason: string
  final: FinalView
  agree: boolean | null
  points: number
  claimed_at: number
  voted_at: number
  /** 仅高级审核员的机器人记录返回 */
  creator?: { uid: number; name: string; friend_code: string } | null
}

export interface ReviewStats {
  total: number
  today: number
  agree_rate: number | null
  judged: number
  integral: number
}

export interface ShopGoods {
  goods_id: number
  goods_name: string
  goods_image: string
  exchange_points: number
  exchange_description: string
  exchange_type: string
  exchange_value: number
}

export interface RobotCard {
  robot_id: number
  name: string
  avatar: string
  chat_num: number
  reported: boolean
  /** 私密可分享（visibility=-1），只有高级审核员查得到 */
  shared: boolean
  /** 运营限流中：在线可聊，但不进搜索推荐 */
  hidden: boolean
}

/** 用户列表卡片（风险用户列表 / 昵称搜索） */
export interface UserCard {
  uid: number
  name: string
  avatar: string
  friend_code: string
  can_create: boolean
  deleted: boolean
  attention: number
  attention_high: boolean
}

export type CanCreateFilter = 'all' | 'on' | 'off'

export interface RobotDetail {
  robot_id: number
  chat_num: number
  report_pending: boolean
  shared: boolean
  hidden: boolean
  /** 下线 / 已删除：只有高级审核员能打开，不能举报 */
  offline: boolean
  deleted: boolean
  creator: { uid: number; name: string; avatar: string }
  hints: ReviewHints
  robot: ReviewRobot
  report: null
  translated: TranslatedContent | null
}

/** 审核单详情：提交审核时的快照 + 最终结果 + 投票（非高级只有自己的票、不带昵称） */
export interface CaseDetail {
  audit_id: number
  robot_id: number
  kind: 'audit' | 'report'
  submitted_at: number
  final: FinalView
  /** 旧快照缺角色设定，用机器人当前设定补上的 */
  attributes_live: boolean
  /** 角色现在还能从搜索详情打开 */
  robot_open: boolean
  creator: { uid: number; name: string; friend_code: string } | null
  votes: RobotReviewCase['votes']
  robot: ReviewRobot
  report: ReviewReport | null
  translated: TranslatedContent | null
}

/** 回看自己的一条 Lorebook / 情景图片投票 */
export interface ItemRecordDetail {
  kind: 'lorebook' | 'lorebook_report' | 'scene_image'
  final: FinalView
  my_vote: ReviewRecord['my_vote']
  my_reason: string
  voted_at: number
  submitted_at: number
  lorebook?: LorebookView
  report?: ReviewReport | null
  image?: SceneImageView
  robot?: Pick<ReviewRobot, 'name' | 'avatar' | 'scene'>
  translated: TranslatedContent | null
}

/** 机器人审核历史的一张单：每位审核员（含见习影子票）的投票，只给高级审核员 */
export interface RobotReviewCase {
  audit_id: number
  kind: 'audit' | 'report'
  submitted_at: number
  final: FinalView
  votes: {
    uid: number
    name: string
    friend_code: string
    violation: boolean
    reason: string
    at: number
    /** 见习影子票：不计入结果 */
    probation: boolean
    agree: boolean | null
  }[]
}

export interface UserProfile {
  /** 对方也是审核员时返回（stage 0 申请中 / 1 见习 / 2 正式） */
  reviewer: { stage: number; advanced: boolean; disabled: boolean } | null
  uid: number
  name: string
  avatar: string
  friend_code: string
  joined_at: number
  deleted: boolean
  lang: string
  can_create: boolean
  link_share: boolean
  attention: number
  attention_limit: number
  attention_high: boolean
  violations: { review: { reason: string; count: number }[]; report: { reason: string; count: number }[] }
  public_robots: number
  shared_robots: number
  log: { audit_user_id: number; operator: string; operate: number; type: number; create_time: number }[]
}

export interface UserCase {
  audit_id: number
  robot_id: number
  name: string
  avatar: string
  at: number
  reason: string
}

export interface ShopOrder {
  exchange_id: number
  exchange_sn: string
  goods_name: string
  goods_image: string
  integral: number
  exchange_quantity: number
  exchange_status: 0 | 10 | 20
  gift_to: string | null
  operating_remark: string
  create_time: number
  handle_time: number
}

export interface IntegralLog {
  integral_id: number
  integral: number
  reason: string
  channel: string
  change_time: number
}

export interface ReviewNotice { id: number; status: 0 | 1; content: string; created_at: number }

/** 后端业务错误：status 为 hiwaifu_php Review* Pkg 的 CODE_* */
export class ReviewApiError extends Error {
  constructor(public status: number, message: string, public data: any) {
    super(message)
  }
}

export const REVIEW_CODE = {
  NOT_REVIEWER: 40301,
  STAGE_DENIED: 40302,
  DISABLED: 40303,
  TASK_GONE: 41001,
  EXAM_LIMIT: 42901,
  EXAM_EMPTY: 50301,
  RULES_UNREAD: 40306,
  BUSY: 42902,
  NOT_ENOUGH: 42203,
  JOINT_VIP: 42205,
  NO_RECIPIENT: 41004,
  NOT_FOUND: 41003,
  REPORT_PENDING: 42206,
  NOT_ADVANCED: 40304,
} as const

async function call<T>(url: string, method: 'get' | 'post', payload?: Record<string, any>): Promise<T> {
  const res = await newRequest<{ status: number; message: string; data: T }>(
    method === 'get'
      ? { url, method, params: payload ?? {} }
      : { url, method, data: payload ?? {} }
  )
  if (Number(res?.status) !== 200) {
    throw new ReviewApiError(Number(res?.status), res?.message || 'Request failed', res?.data)
  }
  return res.data
}

export const reviewApi = {
  me: () => call<ReviewMe>('/client/review/me', 'get'),
  rules: () => call<ReviewRules>('/client/review/rules', 'get'),
  apply: () => call<ReviewMe>('/client/review/apply', 'post'),
  leave: () => call<ReviewMe & { cleared_integral: number }>('/client/review/leave', 'post'),
  examStart: () => call<ExamPaper>('/client/review/exam/start', 'post'),
  examSubmit: (attempt_id: number, answers: Record<number, ExamAnswer>) =>
    call<ExamResult>('/client/review/exam/submit', 'post', { attempt_id, answers }),
  examHistory: (cursor = 0) => call<{ list: ExamHistoryItem[]; next_cursor: number }>('/client/review/exam/history', 'get', { cursor }),
  examAttempt: (attempt_id: number) => call<{ paper: ExamPaper; result: ExamResult }>('/client/review/exam/attempt', 'get', { attempt_id }),
  rulesRead: () => call<{ rules_read: boolean }>('/client/review/rules/read', 'post'),
  examTranslate: (attempt_id: number, qid: number) =>
    call<TranslatedContent>('/client/review/exam/translate', 'post', { attempt_id, qid }),

  next: () => call<{ task: ReviewTask | null; progress: TaskProgress }>('/client/review/task/next', 'post'),
  submit: (data: { token: string; violation: boolean; reason?: string; positions?: string[]; note?: string; nsfw?: boolean; home?: boolean }) =>
    call<SubmitResult>('/client/review/task/submit', 'post', data),
  skip: (token: string) => call<{ task: ReviewTask | null; progress: TaskProgress }>('/client/review/task/skip', 'post', { token }),
  translate: (token: string) => call<TranslatedContent>('/client/review/translate', 'post', { token }),

  records: (cursor = 0, filter = 'all') =>
    call<{ list: ReviewRecord[]; next_cursor: number }>('/client/review/records', 'get', { cursor, filter }),
  itemRecords: (cursor = 0) => call<{ list: ReviewRecord[]; next_cursor: number }>('/client/review/records/items', 'get', { cursor }),
  stats: () => call<ReviewStats>('/client/review/stats', 'get'),
  notices: (page = 1) => call<{ list: ReviewNotice[]; total: number }>('/client/review/notices', 'get', { page }),
  readNotices: () => call<null>('/client/review/notices/read', 'post'),

  goods: () => call<ShopGoods[]>('/client/review/shop/goods', 'get'),
  exchange: (goods_id: number, quantity: number, friend_code = '') =>
    call<{ exchange_id: number; status: number; gift: boolean; integral: number }>('/client/review/shop/exchange', 'post', { goods_id, quantity, friend_code }),
  recipient: (friend_code: string) => call<{ uid: number; name: string; avatar: string }>('/client/review/shop/recipient', 'get', { friend_code }),

  robotSearch: (q: string, page = 1) => call<{ list: RobotCard[]; has_more: boolean }>('/client/review/robots/search', 'get', { q, page }),
  robotDetail: (robot_id: number, translate = false) =>
    call<RobotDetail>('/client/review/robots/detail', 'get', translate ? { robot_id, translate: 1 } : { robot_id }),
  caseDetail: (audit_id: number, translate = false) =>
    call<CaseDetail>('/client/review/cases/detail', 'get', translate ? { audit_id, translate: 1 } : { audit_id }),
  recordItem: (id: number, translate = false) =>
    call<ItemRecordDetail>('/client/review/records/item', 'get', translate ? { id, translate: 1 } : { id }),
  robotReport: (data: { robot_id: number; reason: string; positions: string[]; note?: string }) =>
    call<{ audit_id: number }>('/client/review/robots/report', 'post', data),

  userLookup: (q: string) => call<UserProfile>('/client/review/users/lookup', 'get', { q }),
  userAttention: (canCreate: CanCreateFilter, cursor: string) =>
    call<{ list: UserCard[]; next_cursor: string }>('/client/review/users/attention', 'get', { can_create: canCreate, cursor }),
  userSearch: (name: string, page: number) =>
    call<{ list: UserCard[]; has_more: boolean }>('/client/review/users/search', 'get', { name, page }),
  userRobots: (uid: number, scope: 'public' | 'shared', cursor = 0) =>
    call<{ list: RobotCard[]; next_cursor: number }>('/client/review/users/robots', 'get', { uid, scope, cursor }),
  userCases: (uid: number, kind: 'rejected' | 'reported', cursor = 0) =>
    call<{ list: UserCase[]; next_cursor: number }>('/client/review/users/cases', 'get', { uid, kind, cursor }),
  userReviews: (uid: number, source: 'formal' | 'probation', cursor = 0) =>
    call<{ list: ReviewRecord[]; next_cursor: number }>('/client/review/users/reviews', 'get', { uid, source, cursor }),
  robotReviews: (robot_id: number, cursor = 0) =>
    call<{ list: RobotReviewCase[]; next_cursor: number }>('/client/review/robots/reviews', 'get', { robot_id, cursor }),
  lorebookView: (lorebook_id: number, lang?: string) =>
    call<AttachedLorebook>('/client/review/lorebook/view', 'get', lang ? { lorebook_id, lang } : { lorebook_id }),
  userCanCreate: (uid: number, enable: boolean) => call<{ can_create: boolean }>('/client/review/users/can-create', 'post', { uid, enable }),
  userLinkShare: (uid: number, enable: boolean) => call<{ link_share: boolean }>('/client/review/users/link-share', 'post', { uid, enable }),
  orders: (page = 1) => call<ShopOrder[]>('/client/review/shop/orders', 'get', { page }),
  integralLogs: (cursor = 0) => call<{ list: IntegralLog[]; next_cursor: number }>('/client/review/integral/logs', 'get', { cursor }),
}
